import React, {
  useState,
  createContext,
  useContext,
  useEffect,
  useRef,
  useCallback,
} from "react";
import Drawer from "react-modern-drawer";
import "react-modern-drawer/dist/index.css";
import ChatGPT, {
  ChatGPTHandle,
  showChatRooms,
  QuizSummary,
  CodeSummary,
} from "@quark/chat-gpt";
import "@quark/chat-gpt/dist/index.css";
import { UserInfo } from "../../types";
import { getUserInfo } from "../../api";
import { LOGIN_EVENT } from "../../constants";
import { Modal } from "@quark/design";
import { QRCodeCanvas } from "qrcode.react";
import styles from "./index.module.scss";

export interface ChatGptDrawerProviderProps {
  courseNo: string;
  children: React.ReactNode;
}

export type ChatGptDrawerContextType = {
  sendQuizPrompt: (summary: QuizSummary) => void;
  sendCodePrompt: (summary: CodeSummary) => void;
};
const ChatGptDrawerContext = createContext<ChatGptDrawerContextType>({
  sendQuizPrompt: () => {},
  sendCodePrompt: () => {},
});

export const useChatGptDrawerContext = () => {
  return useContext<ChatGptDrawerContextType>(ChatGptDrawerContext);
};

export const ChatGptDrawerProvider = ({
  courseNo,
  children,
}: ChatGptDrawerProviderProps) => {
  const [open, setOpen] = useState(false);
  const [showVipUpgradeModal, setShowVipUpgradeModal] = useState(false);
  const [roomKey, setRoomKey] = useState<string>("");
  const [userInfo, setUserInfo] = useState<UserInfo>();
  const chatGpt = useRef<ChatGPTHandle>(null);

  useEffect(() => {
    getUserInfo().then(setUserInfo);
  }, []);

  useEffect(() => {
    if (!courseNo) {
      return;
    }
    showChatRooms(courseNo)
      .then((rooms) => {
        if (!rooms.length) {
          return;
        }
        setRoomKey(rooms[0].roomKey);
      })
      .catch(() => {
        // has error
      });
  }, [courseNo]);

  const handleSendQuizPrompt = useCallback((summary: QuizSummary) => {
    if (!chatGpt.current) {
      return;
    }
    chatGpt.current.execQuizPrompt(summary);
    setOpen(true);
  }, []);

  const handleSendCodePrompt = useCallback((summary: CodeSummary) => {
    if (!chatGpt.current) {
      return;
    }
    chatGpt.current.execCodePrompt(summary);
    setOpen(true);
  }, []);

  const handleCloseDrawer = useCallback(() => {
    setOpen(false);
  }, []);

  const handleLogin = useCallback(() => {
    setOpen(false);
    window.dispatchEvent(new Event(LOGIN_EVENT));
  }, []);
  const handleQuotaUsedUp = useCallback(() => {
    setOpen(false);
    setShowVipUpgradeModal(true);
  }, []);
  return (
    <ChatGptDrawerContext.Provider
      value={{
        sendQuizPrompt: handleSendQuizPrompt,
        sendCodePrompt: handleSendCodePrompt,
      }}
    >
      {children}
      <Drawer
        direction="right"
        size="460px"
        open={open}
        onClose={handleCloseDrawer}
        zIndex={9999}
      >
        {roomKey && (
          <ChatGPT
            roomKey={roomKey}
            ref={chatGpt}
            userHeadshot={userInfo?.avatar}
            onLogin={handleLogin}
            onQuotaUsedUp={handleQuotaUsedUp}
          />
        )}
      </Drawer>
      <Modal
        onClose={() => setShowVipUpgradeModal(false)}
        visible={showVipUpgradeModal}
        title="升级成为VIP用户 解锁全系列课程"
        contentClassName={styles.upgradeVip}
      >
        <QRCodeCanvas
          fgColor="#B28241"
          size={240}
          value="https://mall.py2fun.com/buy/#/vip/open"
        />
        <div className={styles.tips}>
          请用微信扫一扫二维码 <br /> 付款成功后请刷新当前页面
        </div>
      </Modal>
    </ChatGptDrawerContext.Provider>
  );
};
